import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  ImportSpecificationDocument,
  type ImportSpecificationQuery,
} from "@/graphql/generated/operations";
import { parseImportSpecLimits, type ImportSpecLimits } from "./import-specification";

/**
 * The deployed importer's own description, read once for the whole import page.
 *
 * Both the drop zone and the format panel quote numbers out of it, and both
 * must quote the *same* deployment. Sharing one hook (and with it Apollo's one
 * cache entry for the query) is what makes that structural rather than a matter
 * of keeping two call sites in step.
 *
 * `limits` is null until the answer is in, or when a schema shape Spark cannot
 * read arrives. Callers must then say nothing about the limits instead of
 * falling back to a number of their own: a stale constant would be indis-
 * tinguishable from a fact about the running importer.
 */
export interface UseImportSpecificationResult {
  spec: ImportSpecificationQuery["importSpecification"] | null;
  limits: ImportSpecLimits | null;
  error: boolean;
}

export function useImportSpecification(): UseImportSpecificationResult {
  const { data, error } = useQuery(ImportSpecificationDocument);
  const spec = data?.importSpecification ?? null;
  const limits = useMemo(() => (spec ? parseImportSpecLimits(spec.jsonSchema) : null), [spec]);

  return { spec, limits, error: Boolean(error) };
}
